import type { Highcharts } from "../useHighcharts"
import { CROSSHAIR_COLOR, CROSSHAIR_DOT_Z_INDEX } from "./plotOverlays"

const DOT_RADIUS = 3

type Endpoint = { x: number; y: number }

/** One rendered dot. Named off the renderer rather than imported, because
 * Highcharts' own `SVGElement` shares its name with the DOM's. */
type Dot = ReturnType<Highcharts.Chart["renderer"]["circle"]>

/**
 * The two things an axis has to tell us that its published type does not: the
 * crosshair element it drew, and which side of the plot it sits on. Both are
 * long-standing Highcharts behaviour — `opposite` is a documented axis option
 * — they are simply absent from the shipped `Axis` interface.
 */
type AxisWithCrosshair = {
  cross?: { element: SVGPathElement }
  opposite?: boolean
}

/**
 * Where a crosshair meets its axis.
 *
 * Read off the drawn element rather than recomputed from the hovered point:
 * whatever Highcharts decided — snapping, a shared tooltip, an inverted axis —
 * the dot lands on the line that is actually on screen. Both ends come from
 * the path too, so nothing here has to agree with `plotLeft`/`plotHeight`
 * separately.
 *
 * A crosshair path is always two points (`M x y L x y`); the vertical one
 * shares an x and the horizontal one shares a y, which is what tells them
 * apart.
 *
 * `opposite` picks which end of a horizontal crosshair to sit on: a chart with
 * a second y-axis draws one crosshair per axis, and the right-hand axis's line
 * has to be marked at its right end or the dot lands on the other axis.
 */
function endpointOf(path: SVGPathElement, opposite: boolean): Endpoint | null {
  const d = path.getAttribute("d")
  if (!d) return null
  const n = d.match(/-?[\d.]+/g)?.map(Number)
  if (!n || n.length < 4) return null
  // The length guard above is what makes these four defined; the compiler
  // can't carry that through an index into a plain `number[]`.
  const [x1, y1, x2, y2] = n as [number, number, number, number]
  // Vertical → x axis, marked at the foot of the line.
  if (Math.abs(x1 - x2) < 0.5) return { x: x1, y: Math.max(y1, y2) }
  // Horizontal → y axis, marked at whichever end its own axis is on.
  if (Math.abs(y1 - y2) < 0.5) {
    return { x: opposite ? Math.max(x1, x2) : Math.min(x1, x2), y: y1 }
  }
  return null
}

/**
 * Marks where each hover crosshair meets its axis with a solid dot.
 *
 * The dotted lines alone leave the reader tracing a faint rule to the edge of
 * the plot to see which date and which value they are on. A filled dot sitting
 * on the axis answers both at a glance.
 *
 * Highcharts has no option for this, so the dots are drawn with its own
 * renderer — which means they live inside the chart's SVG and are destroyed
 * with it, rather than being absolutely-positioned DOM chasing the chart's
 * geometry.
 */
export function attachCrosshairEndpoints(chart: Highcharts.Chart): void {
  const container = chart.container
  if (!container) return

  // Grown on demand rather than fixed: a chart draws one crosshair per axis,
  // so a second y-axis means three, not two. A fixed pair silently left the
  // last one unmarked.
  const dots: Dot[] = []
  const dotAt = (index: number) => {
    const existing = dots[index]
    if (existing) return existing
    const created = chart.renderer
      .circle(0, 0, DOT_RADIUS)
      .attr({ fill: CROSSHAIR_COLOR, zIndex: CROSSHAIR_DOT_Z_INDEX })
      .add()
    dots[index] = created
    return created
  }

  let frame: number | null = null
  const sync = () => {
    frame = null
    // Highcharts deletes a chart's properties on destroy, and a frame queued
    // just before that (e.g. on navigation) still runs
    if (!chart.axes) return
    // Read after Highcharts has drawn this frame's crosshairs; reading in the
    // same tick as the pointer event gives the previous position.
    //
    // Walked per axis rather than by querying the container, because the DOM
    // order of crosshair paths does not say which axis each one belongs to —
    // and for a horizontal crosshair that is exactly what decides which end of
    // it to mark.
    let used = 0
    for (const axis of chart.axes) {
      // Highcharts keeps a crosshair in the DOM once drawn and merely hides
      // it, so presence is not enough: a hidden line has to count as no line,
      // or the dots outlive the crosshairs they belong to.
      const { cross, opposite } = axis as unknown as AxisWithCrosshair
      const element = cross?.element
      if (!element || element.getAttribute("visibility") === "hidden") continue
      const point = endpointOf(element, Boolean(opposite))
      if (!point) continue
      dotAt(used++).attr({ cx: point.x, cy: point.y }).show()
    }
    for (let i = used; i < dots.length; i++) dots[i]?.hide()
  }

  const onMove = () => {
    if (frame != null) return
    frame = requestAnimationFrame(sync)
  }
  const onLeave = () => {
    // Drop the pending frame first: it was queued against a position the
    // pointer has already left, and letting it run would redraw the dots
    // right after they were hidden.
    if (frame != null) {
      cancelAnimationFrame(frame)
      frame = null
    }
    dots.forEach((dot) => dot.hide())
  }

  container.addEventListener("mousemove", onMove)
  container.addEventListener("mouseleave", onLeave)
}
